/**
 * QuickBuy Filters Manager
 * Handles GraphQL-based filtering for collection products
 */

/** Product type filter: display order 1, 2, 3, … (company-defined) */
/* Locked visitors must not see facet counts — they reveal catalogue
   depth per brand/type. Real figures are replaced before they reach
   the DOM; CSS blurs the placeholder for the locked look. */
const qbFmtCount = (c) => (window.QB_LOCKED === true ? '(\u2013\u2013)' : `(${c})`);

const PRODUCT_TYPE_ORDER = [
	'Eau de Parfum',
	'Eau de Toilette',
	'Eau de Cologne',
	'Body Products',
	'Hair Care',
	'Gift Sets',
	'Candles & Roomscents'
];

/**
 * "More filters" tag split: two dependent groups (counts update when the other group is selected).
 * Tag labels must match Shopify product tags (case-insensitive).
 * Group A merges `new` and `new_item` into one "New" filter option.
 */
const MORE_FILTERS_TAG_GROUP_A = ['new', 'new_item'];
const MORE_FILTERS_TAG_GROUP_A_LABEL = 'New';
const MORE_FILTERS_TAG_GROUP_B = ['promo'];

/** Internal/system tags — never show under "More filters". */
const MORE_FILTERS_EXCLUDED_TAGS = ['allowed_active'];

const FILTER_TAG_GROUP_A_ID = 'filter.p.tag.qb_group_a';
const FILTER_TAG_GROUP_B_ID = 'filter.p.tag.qb_group_b';

/** True for Shopify product tag filters (not substring "tag" in unrelated filter ids). */
function isShopifyTagFilterKey(filterId) {
	const id = String(filterId || '');
	return id === 'filter.p.tag' || id.startsWith('filter.p.tag.');
}

/** Escape dynamic strings for double-quoted HTML attributes (vendor names may contain & or "). */
function escapeHtmlAttr(str) {
	return String(str ?? '')
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/\r/g, '&#13;')
		.replace(/\n/g, '&#10;');
}

function escapeHtmlText(str) {
	return String(str ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

/** Match checkbox label to GraphQL / Shopify (trim, Unicode NFC). */
function normalizeFilterValueFromDom(str) {
	if (str == null) return '';
	let s = String(str).trim();
	if (typeof s.normalize === 'function') {
		try {
			s = s.normalize('NFC');
		} catch (e) {
			/* ignore */
		}
	}
	return s;
}

/** Stable key for merging vendor rows (avoids duplicate brands from casing/whitespace drift). */
function vendorMergeKey(label) {
	const n = normalizeFilterValueFromDom(label);
	return n ? n.toLowerCase() : '';
}

/** Match Storefront facet `value.label` to DOM `data-filter-value` (escaping/Unicode safe). */
function facetLabelsEqual(a, b) {
	return vendorMergeKey(a) === vendorMergeKey(b);
}

function isLabelInTagGroup(label, group) {
	if (!label || !Array.isArray(group)) return false;
	const n = String(label).trim().toLowerCase();
	return group.some((t) => String(t).trim().toLowerCase() === n);
}

function isExcludedMoreFilterTag(label) {
	const n = String(label || '').trim().toLowerCase();
	return MORE_FILTERS_EXCLUDED_TAGS.some((t) => String(t).trim().toLowerCase() === n);
}

function filterMoreFilterTagValues(values) {
	return (values || []).filter((v) => v && v.label && !isExcludedMoreFilterTag(v.label));
}

function normalizeTagLabel(value) {
	return String(value || '').trim().toLowerCase();
}

function getActiveFilterTagLabels(value) {
	if (!value) return [];
	if (typeof value === 'string') return [normalizeTagLabel(value)];
	if (typeof value === 'object' && Array.isArray(value.tags) && value.tags.length) {
		return value.tags.map(normalizeTagLabel).filter(Boolean);
	}
	if (typeof value === 'object' && value.value) return [normalizeTagLabel(value.value)];
	return [];
}

function productHasAnyTag(product, tagLabels) {
	if (!tagLabels || !tagLabels.length) return true;
	const productTags = new Set(
		(Array.isArray(product.tags) ? product.tags : []).map(normalizeTagLabel)
	);
	return tagLabels.some((tag) => productTags.has(tag));
}

function productMatchesActiveTagFilterGroups(product, activeFilters, options = {}) {
	const excludeGroup = options.excludeGroup;
	const groups = [
		{ id: FILTER_TAG_GROUP_A_ID, exclude: 'a' },
		{ id: FILTER_TAG_GROUP_B_ID, exclude: 'b' }
	];

	for (const group of groups) {
		if (excludeGroup === group.exclude) continue;
		const values = activeFilters[group.id];
		if (!values || !values.length) continue;
		const selectedTags = values.flatMap(getActiveFilterTagLabels).filter(Boolean);
		if (!selectedTags.length) continue;
		if (!productHasAnyTag(product, selectedTags)) return false;
	}

	return true;
}

class QuickBuyFiltersManager {
	constructor(options = {}) {
		this.resultsFilter = options.resultsFilter;
		this.onFilterChange = options.onFilterChange || (() => {});
		this.searchManager = options.searchManager || null;

		window.qbFiltersManager = this;

		this.allFilters = [];
		this.activeFilters = {};
		this.currentSortKey = 'TITLE'; // Default sort
		this.currentPageSize = 48; // Default page size
		this.filterValuesOverride = {
			vendor: null,
			productType: null,
			tag: null
		};
		this.forceRebuild = false;
		/** Full collection product nodes (min fields) for dependent tag counts */
		this.filterDependencyCatalog = null;
		/** Previous Brand / Product type selection — when it changes, theme refetches collection facets (vendor search mode). */
		this._prevFacetDimensionSignature = undefined;

	}

	/**
	 * Stable signature for filters that affect Storefront facet counts (Brand, Product type).
	 * More filters (tags) are intentionally excluded — tag-only changes should not refetch facets.
	 */
	buildFacetDimensionSignature(active) {
		const af = active || {};
		const parts = [];
		Object.keys(af).forEach((filterId) => {
			const fid = String(filterId);
			const isVendorDim = fid.includes('vendor');
			const isProductTypeDim = fid.includes('product_type') || fid.includes('productType');
			if (!isVendorDim && !isProductTypeDim) return;
			const vals = af[filterId];
			if (!vals || !vals.length) return;
			const flat = vals
				.map((v) => (typeof v === 'string' ? v : v && typeof v === 'object' ? v.value : null))
				.filter(Boolean)
				.map((s) => String(s).trim().toLowerCase())
				.sort();
			if (!flat.length) return;
			parts.push(`${fid}:${flat.join(',')}`);
		});
		return parts.sort().join('|');
	}

	/**
	 * Build filters HTML from GraphQL filters
	 */
	buildFiltersHTML(filters) {
		if (!filters || filters.length === 0) return '';

		let html = '<form data-role="qb-filter-form" id="QuickBuyFilterForm">';

		// Add sorting dropdown at the top
		html += this.buildSortingDropdown();

		// Add Clear Filters button at the top (will be shown/hidden based on selections)
		html += this.buildClearFiltersButton();

		const isBrandLike = (filter) => {
			if (!filter) return false;
			const label = (filter.label || '').toLowerCase();
			return label.includes('brand') || label.includes('vendor') || (filter.id && String(filter.id).includes('vendor'));
		};

		const isProductTypeLike = (filter) => {
			if (!filter) return false;
			const label = (filter.label || '').toLowerCase();
			return label.includes('product type') || (filter.id && String(filter.id).includes('product_type'));
		};

		const isTagLike = (filter) => {
			if (!filter) return false;
			const label = (filter.label || '').toLowerCase();
			return label.includes('more filters') || (filter.id && String(filter.id).includes('tag'));
		};

		// Find Brand filter first
		const brandFilter = filters.find((f) => isBrandLike(f));

		const vendorOverride = this.filterValuesOverride.vendor;
		const fallbackBrandFilter = (!brandFilter && Array.isArray(vendorOverride) && vendorOverride.length)
			? { id: 'filter.p.vendor', label: ((window.QB_I18N||{}).filter_brand) || 'Brand', type: 'LIST', values: [] }
			: null;

		const brandFilterSource = brandFilter || fallbackBrandFilter;

		if (brandFilterSource) {
			let brandFilterToRender = brandFilterSource;
			if (Array.isArray(vendorOverride) && vendorOverride.length) {
				const mergedMap = new Map();

				vendorOverride.forEach((value) => {
					if (!value || !value.label) return;
					const k = vendorMergeKey(value.label);
					if (!k) return;
					const displayLabel = normalizeFilterValueFromDom(value.label);
					mergedMap.set(k, {
						label: displayLabel,
						count: value.count,
						input: value.input || { productVendor: displayLabel }
					});
				});

				if (Array.isArray(brandFilterSource.values)) {
					brandFilterSource.values.forEach((value) => {
						if (!value || !value.label) return;
						const k = vendorMergeKey(value.label);
						if (!k) return;
						const displayLabel = normalizeFilterValueFromDom(value.label);
						const existing = mergedMap.get(k);
						if (existing) {
							existing.count = Math.max(existing.count || 0, value.count || 0);
							if (value.input) existing.input = value.input;
							existing.label = displayLabel;
						} else {
							mergedMap.set(k, {
								label: displayLabel,
								count: value.count,
								input: value.input || { productVendor: displayLabel }
							});
						}
					});
				}

				const mergedValues = Array.from(mergedMap.values()).sort((a, b) =>
					a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
				);

				brandFilterToRender = Object.assign({}, brandFilterSource, {
					values: mergedValues
				});
			}

			html += this.buildFilterGroup(brandFilterToRender, true);
		}

		// Render other filters (skip brand/vendor duplicates)
		filters.forEach(filter => {
			if (filter.type !== 'LIST' && filter.type !== 'BOOLEAN') return;
			if (isBrandLike(filter)) return;

			/* 07.09.2026: Gruppen-Titel lokalisieren — das Shopify-Faceten-Label
			   ("Produkttyp") ist einsprachig gepflegt und erschien sonst auch
			   auf der englischen Seite. */
			if (isProductTypeLike(filter)) {
				filter = Object.assign({}, filter, { label: ((window.QB_I18N || {}).filter_product_type) || filter.label });
			}

			if (isProductTypeLike(filter) && Array.isArray(this.filterValuesOverride.productType)) {
				const merged = this.mergeOverrideIntoFilter(filter, this.filterValuesOverride.productType, {
					labelKey: 'Product type',
					displayLabel: ((window.QB_I18N||{}).filter_product_type) || 'Product type',
					inputBuilder: (label) => ({ productType: label })
				});
				html += this.buildFilterGroup(merged, false);
				return;
			}

			if (isTagLike(filter) && Array.isArray(this.filterValuesOverride.tag)) {
				const merged = this.mergeOverrideIntoFilter(filter, this.filterValuesOverride.tag, {
					labelKey: 'More filters',
					displayLabel: ((window.QB_I18N||{}).filter_more) || 'More filters',
					inputBuilder: (label) => ({ tag: label })
				});
				const { valuesA, valuesB } = this.splitMoreFilterTagValues(merged.values);
				if (valuesA.length || valuesB.length) {
					html += this.buildMoreFiltersSplitHTML(merged, valuesA, valuesB);
				} else {
					html += this.buildFilterGroup(merged, false);
				}
				return;
			}

			html += this.buildFilterGroup(filter, false);
		});

		// Fallback groups if Shopify doesn't provide filters
		if (!filters.some((f) => isProductTypeLike(f)) && Array.isArray(this.filterValuesOverride.productType) && this.filterValuesOverride.productType.length) {
			const fallback = this.mergeOverrideIntoFilter(
				{ id: 'filter.p.product_type', label: ((window.QB_I18N||{}).filter_product_type) || 'Product type', type: 'LIST', values: [] },
				this.filterValuesOverride.productType,
				{ labelKey: 'Product type', displayLabel: ((window.QB_I18N||{}).filter_product_type) || 'Product type', inputBuilder: (label) => ({ productType: label }) }
			);
			html += this.buildFilterGroup(fallback, false);
		}

		if (!filters.some((f) => isTagLike(f)) && Array.isArray(this.filterValuesOverride.tag) && this.filterValuesOverride.tag.length) {
			const fallback = this.mergeOverrideIntoFilter(
				{ id: 'filter.p.tag', label: ((window.QB_I18N||{}).filter_more) || 'More filters', type: 'LIST', values: [] },
				this.filterValuesOverride.tag,
				{ labelKey: 'More filters', displayLabel: ((window.QB_I18N||{}).filter_more) || 'More filters', inputBuilder: (label) => ({ tag: label }) }
			);
			const { valuesA, valuesB } = this.splitMoreFilterTagValues(fallback.values);
			if (valuesA.length || valuesB.length) {
				html += this.buildMoreFiltersSplitHTML(fallback, valuesA, valuesB);
			} else {
				html += this.buildFilterGroup(fallback, false);
			}
		}

		html += '</form>';
		return html;
	}

	mergeOverrideIntoFilter(filterSource, overrideValues, options = {}) {
		const mergedMap = new Map();
		overrideValues.forEach((value) => {
			if (!value || !value.label) return;
			mergedMap.set(value.label, {
				label: value.label,
				count: value.count,
				input: value.input || (options.inputBuilder ? options.inputBuilder(value.label) : null)
			});
		});

		if (Array.isArray(filterSource.values)) {
			filterSource.values.forEach((value) => {
				if (!value || !value.label) return;
				const existing = mergedMap.get(value.label);
				if (existing) {
					existing.count = value.count;
					if (value.input) existing.input = value.input;
				} else {
					mergedMap.set(value.label, {
						label: value.label,
						count: value.count,
						input: value.input || (options.inputBuilder ? options.inputBuilder(value.label) : null)
					});
				}
			});
		}

		let mergedValues = Array.from(mergedMap.values());
		if (options.labelKey === 'Product type' && PRODUCT_TYPE_ORDER.length) {
			const orderMap = new Map(PRODUCT_TYPE_ORDER.map((label, i) => [label.toLowerCase(), i]));
			mergedValues = mergedValues.sort((a, b) => {
				const ai = orderMap.get((a.label || '').toLowerCase());
				const bi = orderMap.get((b.label || '').toLowerCase());
				if (ai !== undefined && bi !== undefined) return ai - bi;
				if (ai !== undefined) return -1;
				if (bi !== undefined) return 1;
				return (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' });
			});
		} else {
			mergedValues.sort((a, b) =>
				a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
			);
		}

		if (options.labelKey === 'More filters') {
			mergedValues = filterMoreFilterTagValues(mergedValues);
		}

		return Object.assign({}, filterSource, {
			label: filterSource.label || options.displayLabel || options.labelKey || ((window.QB_I18N||{}).filter_generic) || 'Filter',
			values: mergedValues
		});
	}

	/**
	 * Split merged tag values into two dependent groups (see MORE_FILTERS_TAG_GROUP_A / B).
	 */
	splitMoreFilterTagValues(values) {
		const valuesA = [];
		const valuesB = [];
		filterMoreFilterTagValues(values).forEach((v) => {
			if (!v || !v.label) return;
			if (isLabelInTagGroup(v.label, MORE_FILTERS_TAG_GROUP_A)) valuesA.push(v);
			else if (isLabelInTagGroup(v.label, MORE_FILTERS_TAG_GROUP_B)) valuesB.push(v);
		});
		return {
			valuesA: valuesA.length ? [this.mergeMoreFilterTagGroup(valuesA, MORE_FILTERS_TAG_GROUP_A_LABEL, MORE_FILTERS_TAG_GROUP_A)] : [],
			valuesB
		};
	}

	mergeMoreFilterTagGroup(values, displayLabel, tagLabels) {
		const tags = (tagLabels || []).map((label) => String(label).trim()).filter(Boolean);
		const count = values.reduce((max, value) => Math.max(max, value.count || 0), 0);
		return {
			label: displayLabel,
			displayLabel,
			tags,
			count,
			input: tags[0] ? { tag: tags[0] } : null
		};
	}

	/**
	 * One "More filters" block with two subgroups (dependent counts).
	 */
	buildMoreFiltersSplitHTML(merged, valuesA, valuesB) {
		const label = merged.label || ((window.QB_I18N||{}).filter_more) || 'More filters';
		const selA = this.activeFilters[FILTER_TAG_GROUP_A_ID]?.length || 0;
		const selB = this.activeFilters[FILTER_TAG_GROUP_B_ID]?.length || 0;
		const selectedTotal = selA + selB;
		const badge =
			selectedTotal > 0
				? `<div class="selected-count"><span>(${selectedTotal})</span></div>`
				: '';

		let html = `<details class="qb-filter-group qb-more-filters-outer" open>`;
		html += `<summary><span>${escapeHtmlText(label)}</span>${badge}</summary>`;
		html += `<div class="qb-more-filters-inner">`;

		if (valuesA.length) {
			html += `<div class="qb-filter-subgroup" data-tag-subgroup="a" data-filter-id="${FILTER_TAG_GROUP_A_ID}">`;
			html += '<div class="qb-filter-values">';
			valuesA.forEach((value) => {
				html += this.buildMoreFilterCheckboxRow(FILTER_TAG_GROUP_A_ID, label, value);
			});
			html += '</div></div>';
		}

		if (valuesB.length) {
			html += `<div class="qb-filter-subgroup" data-tag-subgroup="b" data-filter-id="${FILTER_TAG_GROUP_B_ID}">`;
			html += '<div class="qb-filter-values">';
			valuesB.forEach((value) => {
				html += this.buildMoreFilterCheckboxRow(FILTER_TAG_GROUP_B_ID, label, value);
			});
			html += '</div></div>';
		}

		html += '</div></details>';
		return html;
	}

	buildMoreFilterCheckboxRow(filterId, filterLabel, value) {
		const count = value.count !== undefined && value.count !== null ? value.count : 0;
		const displayLabel = value.displayLabel || value.label;
		const tagLabels = Array.isArray(value.tags) && value.tags.length ? value.tags : [displayLabel];
		const inputAttr = value.input
			? `data-filter-input="${encodeURIComponent(JSON.stringify(value.input))}"`
			: '';
		const tagsAttr =
			tagLabels.length > 1
				? `data-filter-tags="${escapeHtmlAttr(JSON.stringify(tagLabels))}"`
				: '';
		const fid = escapeHtmlAttr(filterId);
		const flbl = escapeHtmlAttr(filterLabel);
		const vlbl = escapeHtmlAttr(displayLabel);
		const vtext = escapeHtmlText(displayLabel);
		return `
          <label class="qb-filter-option">
            <input type="checkbox"
                   data-filter-id="${fid}"
                   data-filter-label="${flbl}"
                   data-filter-value="${vlbl}"
                   ${tagsAttr}
                   ${inputAttr}
                   value="${vlbl}">
            <div class="qb-filter-option-text-wrapper">
              <span class="qb-filter-option-label">${vtext}</span>
              <span class="qb-filter-option-count">${qbFmtCount(count)}</span>
            </div>
          </label>
        `;
	}

	setFilterDependencyCatalog(products) {
		this.filterDependencyCatalog = Array.isArray(products) ? products : null;
		queueMicrotask(() => {
			if (typeof this.refreshDependentTagCounts === 'function') {
				this.refreshDependentTagCounts();
			}
			if (this.hasActiveFilters() && typeof this.recomputeVendorAndProductTypeCountsFromCatalog === 'function') {
				this.recomputeVendorAndProductTypeCountsFromCatalog();
			}
		});
	}

	/**
	 * Products matching brand/type + opposite group's tag selections only (exclude one group's tags for faceted counts).
	 */
	productMatchesFiltersForTagDep(product, excludeGroup) {
		const normalize = (v) => String(v || '').trim().toLowerCase();
		const vendor = normalize(product.vendor);
		const productType = normalize(product.productType);
		const vendorSet = new Set();
		const typeSet = new Set();

		Object.entries(this.activeFilters).forEach(([filterId, values]) => {
			if (!values || !values.length) return;
			const id = String(filterId);
			const isVendor = id.includes('vendor');
			const isType = id.includes('product_type');
			const isTag = isShopifyTagFilterKey(filterId);
			if (!isVendor && !isType && !isTag) return;
			if (isTag) return;
			values.forEach((value) => {
				const lbl =
					typeof value === 'string'
						? value
						: value && typeof value === 'object'
							? value.value
							: null;
				if (!lbl) return;
				const n = normalize(lbl);
				if (isVendor) vendorSet.add(n);
				if (isType) typeSet.add(n);
			});
		});

		if (vendorSet.size && !vendorSet.has(vendor)) return false;
		if (typeSet.size && !typeSet.has(productType)) return false;
		return productMatchesActiveTagFilterGroups(product, this.activeFilters, { excludeGroup });
	}

	/**
	 * Recompute tag counts for each subgroup; hide 0-count options; hide empty subgroup; sync selection if needed.
	 */
	refreshDependentTagCounts() {
		const catalog = this.filterDependencyCatalog;
		const form = document.getElementById('QuickBuyFilterForm');
		if (!catalog || !catalog.length || !form) return;
		if (this._inDepTagRefresh) return;
		this._inDepTagRefresh = true;

		try {
			const normalize = (v) => String(v || '').trim().toLowerCase();
			const productHasTag = (p, label) => {
				const n = normalize(label);
				return (Array.isArray(p.tags) ? p.tags : []).some((t) => normalize(t) === n);
			};

			const updateGroup = (filterId, excludeGroup) => {
				const pool = catalog.filter((p) => this.productMatchesFiltersForTagDep(p, excludeGroup));
				const inputs = form.querySelectorAll(`input[data-filter-id="${filterId}"]`);
				let anyVisible = false;
				inputs.forEach((checkbox) => {
					const val = checkbox.getAttribute('data-filter-value');
					const opt = checkbox.closest('.qb-filter-option');
					if (!val || !opt) return;
					const tagsRaw = checkbox.getAttribute('data-filter-tags');
					let tagLabels = [val];
					if (tagsRaw) {
						try {
							const parsed = JSON.parse(tagsRaw);
							if (Array.isArray(parsed) && parsed.length) tagLabels = parsed;
						} catch (e) {
							tagLabels = [val];
						}
					}
					const count = pool.filter((p) => tagLabels.some((label) => productHasTag(p, label))).length;
					const countSpan = opt.querySelector('.qb-filter-option-count');
					if (countSpan) countSpan.textContent = `${qbFmtCount(count)}`;
					if (count === 0) {
						opt.hidden = !checkbox.checked;
						// Do not uncheck: clearing the box dropped the GraphQL tag filter and hid promo rows.
					} else {
						opt.hidden = false;
						anyVisible = true;
					}
				});
				const subgroup = form.querySelector(`.qb-filter-subgroup[data-filter-id="${filterId}"]`);
				if (subgroup) subgroup.hidden = !anyVisible;
			};

			updateGroup(FILTER_TAG_GROUP_A_ID, 'a');
			updateGroup(FILTER_TAG_GROUP_B_ID, 'b');

			const subA = form.querySelector('.qb-filter-subgroup[data-tag-subgroup="a"]');
			const subB = form.querySelector('.qb-filter-subgroup[data-tag-subgroup="b"]');
			const outer = form.querySelector('.qb-more-filters-outer');
			if (outer) {
				const hasA = Boolean(subA);
				const hasB = Boolean(subB);
				if (hasA && hasB) outer.hidden = Boolean(subA.hidden && subB.hidden);
				else if (hasA) outer.hidden = Boolean(subA.hidden);
				else if (hasB) outer.hidden = Boolean(subB.hidden);
			}

			const prev = JSON.stringify(this.activeFilters);
			this.activeFilters = this.collectFilterStateFromForm();
			if (Object.keys(this.activeFilters).length === 0) {
				if (window.COLLECTION_TOTAL_COUNT_FROM_GRAPHQL) {
					window.COLLECTION_TOTAL_COUNT = window.COLLECTION_TOTAL_COUNT_FROM_GRAPHQL;
				}
			}
			const next = JSON.stringify(this.activeFilters);
			if (prev !== next) {
				this.updateSelectedCountBadges();
				this.updateClearButton();
				this.onFilterChange();
			}
		} finally {
			this._inDepTagRefresh = false;
		}
	}

	/**
	 * Collect active filter state from checkbox DOM (shared by applyFilters + dependent tag sync).
	 */
	collectFilterStateFromForm() {
		const filterForm = document.getElementById('QuickBuyFilterForm');
		if (!filterForm) return {};

		const activeFilters = {};
		const checkedBoxes = filterForm.querySelectorAll('input[type="checkbox"]:checked');
		checkedBoxes.forEach((checkbox) => {
			const filterId = checkbox.getAttribute('data-filter-id');
			const filterLabel = checkbox.getAttribute('data-filter-label');
			let filterValue = checkbox.getAttribute('data-filter-value');
			if (filterValue == null || filterValue === '') {
				filterValue = checkbox.value;
			}
			filterValue = normalizeFilterValueFromDom(filterValue);
			const filterInputRaw = checkbox.getAttribute('data-filter-input');
			let filterInput = null;
			if (filterInputRaw) {
				try {
					const decoded = decodeURIComponent(filterInputRaw);
					filterInput = JSON.parse(decoded);
					if (typeof filterInput === 'string') {
						filterInput = JSON.parse(filterInput);
					}
				} catch (e) {
					filterInput = null;
				}
			}
			if (!filterInput && filterValue) {
				const lbl = (filterLabel || '').toLowerCase();
				if ((filterId && String(filterId).includes('vendor')) || lbl.includes('brand')) {
					filterInput = { productVendor: filterValue };
				} else if ((filterId && String(filterId).includes('product_type')) || lbl.includes('product type')) {
					filterInput = { productType: filterValue };
				} else if (isShopifyTagFilterKey(filterId) || lbl.includes('more filters')) {
					filterInput = { tag: filterValue };
				}
			}

			const tagsRaw = checkbox.getAttribute('data-filter-tags');
			let tagLabels = null;
			if (tagsRaw) {
				try {
					const parsed = JSON.parse(tagsRaw);
					if (Array.isArray(parsed) && parsed.length) tagLabels = parsed;
				} catch (e) {
					tagLabels = null;
				}
			}

			if (!activeFilters[filterId]) {
				activeFilters[filterId] = [];
			}
			if (tagLabels) {
				activeFilters[filterId].push({
					value: filterValue,
					input: filterInput || (tagLabels[0] ? { tag: tagLabels[0] } : null),
					tags: tagLabels.map((label) => normalizeFilterValueFromDom(label)).filter(Boolean)
				});
				return;
			}
			activeFilters[filterId].push(
				filterInput ? { value: filterValue, input: filterInput } : filterValue
			);
		});

		return activeFilters;
	}

	/**
	 * Build sorting dropdown HTML
	 */
	buildSortingDropdown() {
		if (
			this.currentSortKey === 'BEST_SELLING' ||
			this.currentSortKey === 'CREATED' ||
			this.currentSortKey === 'CREATED_DESC'
		) {
			this.currentSortKey = 'TITLE';
		}
		const i18n = window.QB_I18N || {};
		const t = {
			sortBy: i18n.sort_by || 'Sort by:',
			titleAsc: i18n.sort_title_asc || 'Title (A-Z)',
			titleDesc: i18n.sort_title_desc || 'Title (Z-A)',
			priceAsc: i18n.sort_price_asc || 'Price (Low to High)',
			priceDesc: i18n.sort_price_desc || 'Price (High to Low)',
			pageSize: i18n.page_size || 'Page size:'
		};
		return `
    <div style="display:flex; gap: 12px; align-items:center; justify-content: space-between;">
      <div class="qb-sort-wrapper" style="width: 100%;">
        <label for="QuickBuySortSelect" class="qb-sort-label">
          ${t.sortBy}
        </label>
        <select id="QuickBuySortSelect" class="qb-sort-select">
          <option value="TITLE" ${this.currentSortKey === 'TITLE' ? 'selected' : ''}>${t.titleAsc}</option>
          <option value="TITLE_DESC" ${this.currentSortKey === 'TITLE_DESC' ? 'selected' : ''}>${t.titleDesc}</option>
          <option value="PRICE" ${this.currentSortKey === 'PRICE' ? 'selected' : ''}>${t.priceAsc}</option>
          <option value="PRICE_DESC" ${this.currentSortKey === 'PRICE_DESC' ? 'selected' : ''}>${t.priceDesc}</option>
        </select>
      </div>
      <div class="qb-sort-wrapper" style="width: 120px;">
        <label for="QuickBuyPageSizeSelect" class="qb-sort-label">
          ${t.pageSize}
        </label>
        <select id="QuickBuyPageSizeSelect" class="qb-sort-select">
          <option value="12" ${this.currentPageSize === 12 ? 'selected' : ''}>12</option>
          <option value="24" ${this.currentPageSize === 24 ? 'selected' : ''}>24</option>
          <option value="48" ${this.currentPageSize === 48 ? 'selected' : ''}>48</option>
          <option value="72" ${this.currentPageSize === 72 ? 'selected' : ''}>72</option>
        </select>
      </div>
      </div>
    `;
	}

	/**
	 * Build Clear Filters button HTML
	 */
	buildClearFiltersButton() {
		const totalSelected = this.getTotalSelectedCount();
		const displayStyle = totalSelected > 0 ? '' : 'display: none;';

		return `
      <div class="qb-clear-wrap" id="QuickBuyClearWrap" style="${displayStyle}">
        <button id="QuickBuyClearAll" class="qb-clear-btn" type="button">
          <div>
            <span class="qb-clear-icon" aria-hidden="true">🗑️</span>
            <span>${((window.QB_I18N||{}).filter_clear) || 'Clear filters'}</span>
          </div>
          <span class="selected-count" id="QuickBuyClearCount">
            (${totalSelected})
          </span>
        </button>
      </div>
    `;
	}

	/**
	 * Get total count of all selected filters
	 */
	getTotalSelectedCount() {
		let total = 0;
		for (const filterId in this.activeFilters) {
			if (this.activeFilters[filterId]) {
				total += this.activeFilters[filterId].length;
			}
		}
		return total;
	}

	/**
	 * Build individual filter group HTML
	 */
	buildFilterGroup(filter, isBrand) {
		// Product type: enforce company order (1, 2, 3, …)
		const isProductType = (filter.id && String(filter.id).includes('product_type')) || (filter.label && String(filter.label).toLowerCase().includes('product type'));
		const isTagFilter =
			isShopifyTagFilterKey(filter.id) ||
			(filter.label && String(filter.label).toLowerCase().includes('more filters'));
		if (isTagFilter && filter.values) {
			filter = Object.assign({}, filter, { values: filterMoreFilterTagValues(filter.values) });
		}
		if (filter.values && isProductType && PRODUCT_TYPE_ORDER.length) {
			const orderMap = new Map(PRODUCT_TYPE_ORDER.map((label, i) => [label.toLowerCase(), i]));
			filter = Object.assign({}, filter, {
				values: [...filter.values].sort((a, b) => {
					const ai = orderMap.get((a.label || '').toLowerCase());
					const bi = orderMap.get((b.label || '').toLowerCase());
					if (ai !== undefined && bi !== undefined) return ai - bi;
					if (ai !== undefined) return -1;
					if (bi !== undefined) return 1;
					return (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' });
				})
			});
		}
		const className = isBrand ? 'qb-filter-group qb-filter-brand' : 'qb-filter-group';
		const hasSearch = filter.values && filter.values.length > 7;

		// Calculate selected count for this filter
		const selectedCount = this.activeFilters[filter.id] ? this.activeFilters[filter.id].length : 0;

		let html = `<details class="${className}" open>`;
		const filterLabelAttr = escapeHtmlAttr(filter.label);
		const filterIdAttr = escapeHtmlAttr(filter.id);
		html += `<summary>`;
		html += `<span>${escapeHtmlText(filter.label)}</span>`;

		// Show selected count if any filters are selected
		if (selectedCount > 0) {
			html += `
        <div class="selected-count">
          <span>(${selectedCount})</span>
        </div>
      `;
		}

		html += `</summary>`;

		if (hasSearch) {
			const searchIntoTemplate = ((window.QB_I18N||{}).filter_search_into) || 'Search into {{ name }}';
			const searchIntoPlaceholder = escapeHtmlAttr(searchIntoTemplate.replace('{{ name }}', filter.label || ''));
			html += `
        <div class="qb-filter-search">
          <input type="search" class="qb-filter-search__input"
                 placeholder="${searchIntoPlaceholder}"
                 data-filter-search="1">
        </div>
      `;
		}

		html += '<div class="qb-filter-values">';

		if (filter.values) {
			filter.values.forEach(value => {
				const count = value.count !== undefined && value.count !== null ? value.count : 0;
				const inputAttr = value.input
					? `data-filter-input="${encodeURIComponent(JSON.stringify(value.input))}"`
					: '';
				const selected =
					Array.isArray(this.activeFilters[filter.id]) &&
					this.activeFilters[filter.id].some((item) => {
						const lbl =
							typeof item === 'string'
								? item
								: item && typeof item === 'object'
									? item.value
									: null;
						return lbl === value.label;
					});
				/* 06.09.2026: Optionen ohne Treffer in DIESER Collection verstecken —
				   gilt jetzt auch für Marken, nicht nur Produkttypen. */
				const hideProductTypeZero = (isProductType || isBrand) && count === 0 && !selected;
				const vAttr = escapeHtmlAttr(value.label);
				const vText = escapeHtmlText(value.label);
				html += `
          <label class="qb-filter-option"${hideProductTypeZero ? ' hidden' : ''}>
            <input type="checkbox" 
                   data-filter-id="${filterIdAttr}"
                   data-filter-label="${filterLabelAttr}" 
                   data-filter-value="${vAttr}" 
                   ${inputAttr}
                   value="${vAttr}">
            <div class="qb-filter-option-text-wrapper">
              <span class="qb-filter-option-label">${vText}</span>
              <span class="qb-filter-option-count">${qbFmtCount(count)}</span>
            </div>
          </label>
        `;
			});
		}

		html += '</div>';

		// Add "See more" button for brand filter
	// 	if (isBrand) {
	// 		html += `
    //     <button class="qb-see-more-btn" data-filter-id="${filter.id}" type="button">
    //       <span class="qb-see-more-text">See more</span>
    //       <span class="qb-see-more-arrow">
    //       <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
    //         <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
    //       </svg>
    //       </span>
    //     </button>
    //   `;
	// 	}

		html += '</details>';

		return html;
	}

	/**
	 * Build GraphQL filter input from active filters
	 * @param {Object} [options] - { excludeVendor: boolean, excludeTag: boolean }
	 *   excludeTag: when true, tag filters are omitted (used when tags are applied via search query as OR)
	 */
	buildFilterInput(options = {}) {
		const filterArray = [];
		const excludeVendor = options && options.excludeVendor;
		const excludeTag = options && options.excludeTag;
		const excludeProductType = options && options.excludeProductType;

		for (const [filterId, values] of Object.entries(this.activeFilters)) {
			if (values && values.length > 0) {
				// Extract the field name from filter.id
				// Shopify filter IDs look like: "filter.v.option.variant_option_name" or "filter.p.vendor"
				let graphQLField = filterId;

				// Common mappings based on Shopify's filter structure
				if (filterId.includes('vendor')) {
					graphQLField = 'productVendor';
				} else if (filterId.includes('type')) {
					graphQLField = 'productType';
				} else if (filterId.includes('tag')) {
					graphQLField = 'tag';
				} else if (filterId.startsWith('filter.v.')) {
					// Variant option filter - extract option name
					graphQLField = filterId.replace('filter.v.option.', '');
				} else if (filterId.startsWith('filter.p.')) {
					// Product filter - extract field name
					graphQLField = filterId.replace('filter.p.', '');
				}

				if (excludeVendor && graphQLField === 'productVendor') continue;
				if (excludeProductType && graphQLField === 'productType') continue;
				if (excludeTag && graphQLField === 'tag') continue;

				values.forEach(value => {
					if (value && typeof value === 'object' && value.input) {
						let inputValue = value.input;
						if (typeof inputValue === 'string') {
							try {
								inputValue = JSON.parse(inputValue);
							} catch (e) {
								inputValue = null;
							}
						}
						if (
							inputValue &&
							typeof inputValue === 'object' &&
							Object.keys(inputValue).length > 0
						) {
							filterArray.push(inputValue);
							return;
						}
					}
					const rawValue = typeof value === 'string' ? value : value && value.value ? value.value : null;
					if (!rawValue) return;
					// Create proper filter input object
					const filterInput = {};
					filterInput[graphQLField] = rawValue;
					filterArray.push(filterInput);
				});
			}
		}

		return filterArray;
	}

	/**
	 * Selected brand/vendor labels (for Storefront search query OR-clause).
	 */
	getSelectedVendorValues() {
		const out = [];
		Object.entries(this.activeFilters).forEach(([filterId, values]) => {
			if (!values || !values.length || !String(filterId).includes('vendor')) return;
			values.forEach((value) => {
				const label =
					typeof value === 'string'
						? value
						: value && typeof value === 'object'
							? value.value
							: null;
				const n = label ? normalizeFilterValueFromDom(label) : '';
				if (n) out.push(n);
			});
		});
		return out;
	}

	/**
	 * Selected product type labels (for Storefront search query OR-clause when multiple types are chosen).
	 */
	getSelectedProductTypeLabels() {
		const out = [];
		Object.entries(this.activeFilters).forEach(([filterId, values]) => {
			if (!values || !values.length || !String(filterId).includes('product_type')) return;
			values.forEach((value) => {
				const label =
					typeof value === 'string'
						? value
						: value && typeof value === 'object'
							? value.value
							: null;
				const n = label ? normalizeFilterValueFromDom(label) : '';
				if (n) out.push(n);
			});
		});
		return out;
	}

	/**
	 * Return selected "More filters" (tag) values for OR-query (union of matches)
	 */
	getSelectedTagValues() {
		const out = [];
		for (const [filterId, values] of Object.entries(this.activeFilters)) {
			if (!values || !values.length || !isShopifyTagFilterKey(filterId)) continue;
			values.forEach((value) => {
				if (value && typeof value === 'object' && Array.isArray(value.tags) && value.tags.length) {
					value.tags.forEach((tag) => {
						const n = normalizeFilterValueFromDom(tag);
						if (n && !isExcludedMoreFilterTag(n)) out.push(n);
					});
					return;
				}
				const label = typeof value === 'string' ? value : value && typeof value === 'object' ? value.value : null;
				const n = label ? normalizeFilterValueFromDom(label) : '';
				if (n && !isExcludedMoreFilterTag(n)) out.push(n);
			});
		}
		return out;
	}

	/**
	 * Storefront search query fragment for selected tags — OR within "More filters" (not AND).
	 * Used with collection_id so results stay scoped to the stock-order collection.
	 */
	getMoreFiltersTagSearchClause() {
		const tags = [...new Set(this.getSelectedTagValues())];
		if (!tags.length) return '';
		const escape = (v) => String(v || '').replace(/"/g, '\\"');
		if (tags.length === 1) {
			return `tag:"${escape(tags[0])}"`;
		}
		return `(${tags.map((t) => `tag:"${escape(t)}"`).join(' OR ')})`;
	}

	/**
	 * Convert filter array to GraphQL string format
	 * @param {Object} [options] - passed to buildFilterInput (excludeVendor, excludeTag)
	 */
	getFiltersClause(options) {
		const opts = options || {};
		let filterArray = this.buildFilterInput(opts).filter(
			(f) => f && typeof f === 'object' && Object.keys(f).length > 0
		);
		// If GraphQL would get no filters but the form still has selections, rebuild from labels
		// (avoids empty collection queries + client-only filtering that can show zero rows).
		if (filterArray.length === 0 && this.hasActiveFilters()) {
			filterArray = this.buildFilterInputFromSelections(opts);
		}
		if (filterArray.length === 0) return '';

		// Convert to GraphQL format: filters: [...]
		// Need to properly format for GraphQL (not just JSON.stringify)
		const filtersStr = filterArray
			.filter((filter) => filter && typeof filter === 'object' && Object.keys(filter).length > 0)
			.map((filter) => {
				const entries = Object.entries(filter);
				const filterStr = entries
					.map(([key, value]) => {
						if (typeof value === 'number' || typeof value === 'boolean') {
							return `${key}: ${value}`;
						}
						if (value !== null && typeof value === 'object') {
							return null;
						}
						const stringValue = String(value);
						const escapedValue = stringValue.replace(/"/g, '\\"');
						return `${key}: "${escapedValue}"`;
					})
					.filter(Boolean)
					.join(', ');
				return `{${filterStr}}`;
			})
			.filter((s) => s !== '{}')
			.join(', ');

		if (!filtersStr) return '';
		return `, filters: [${filtersStr}]`;
	}

	/**
	 * Build ProductFilter[] from selected labels when normal buildFilterInput yields nothing.
	 */
	buildFilterInputFromSelections(options) {
		const opts = options || {};
		const out = [];
		if (!opts.excludeVendor) {
			this.getSelectedVendorValues().forEach((v) => {
				if (v) out.push({ productVendor: v });
			});
		}
		if (!opts.excludeProductType) {
			this.getSelectedProductTypeLabels().forEach((t) => {
				if (t) out.push({ productType: t });
			});
		}
		if (!opts.excludeTag) {
			this.getSelectedTagValues().forEach((tag) => {
				if (tag) out.push({ tag });
			});
		}
		return out;
	}

	/**
	 * Update filters UI with new data from GraphQL
	 */
	updateFilters(filters) {
		if (!filters || !this.resultsFilter) return;

		this.allFilters = filters;

		// Only rebuild if filters haven't been built yet
		if (!this.resultsFilter.dataset.initialized || this.forceRebuild) {
			this.resultsFilter.innerHTML = this.buildFiltersHTML(filters);
			this.resultsFilter.dataset.initialized = 'true';
			this.hydrateFilterHandlers();
			this.forceRebuild = false;
		} else {
			// Update filter counts without rebuilding the entire UI
			this.updateFilterCounts(filters);
		}
		
		// Always restore filter state (to maintain selections)
		this.restoreFilterState();

		queueMicrotask(() => {
			if (typeof this.refreshDependentTagCounts === 'function') {
				this.refreshDependentTagCounts();
			}
		});
	}

	setVendorValuesOverride(values) {
		this.filterValuesOverride.vendor = Array.isArray(values) ? values : null;
		this.forceRebuild = true;
		if (this.allFilters.length) {
			this.updateFilters(this.allFilters);
		}
	}

	mergeVendorValuesOverride(values) {
		if (!Array.isArray(values) || values.length === 0) return;
		const merged = new Map();
		(this.filterValuesOverride.vendor || []).forEach((value) => {
			if (!value || !value.label) return;
			const k = vendorMergeKey(value.label);
			if (!k) return;
			const displayLabel = normalizeFilterValueFromDom(value.label);
			merged.set(k, {
				label: displayLabel,
				count: value.count,
				input: value.input || { productVendor: displayLabel }
			});
		});
		values.forEach((value) => {
			if (!value || !value.label) return;
			const k = vendorMergeKey(value.label);
			if (!k) return;
			const displayLabel = normalizeFilterValueFromDom(value.label);
			const existing = merged.get(k);
			if (existing) {
				existing.count = Math.max(existing.count || 0, value.count || 0);
				if (value.input) existing.input = value.input;
				existing.label = displayLabel;
			} else {
				merged.set(k, {
					label: displayLabel,
					count: value.count,
					input: value.input || { productVendor: displayLabel }
				});
			}
		});
		this.filterValuesOverride.vendor = Array.from(merged.values());
		this.forceRebuild = false;
		if (this.allFilters.length) {
			this.updateFilters(this.allFilters);
		}
	}

	setProductTypeValuesOverride(values) {
		this.filterValuesOverride.productType = Array.isArray(values) ? values : null;
		this.forceRebuild = true;
		if (this.allFilters.length) {
			this.updateFilters(this.allFilters);
		}
	}

	mergeProductTypeValuesOverride(values) {
		if (!Array.isArray(values) || values.length === 0) return;
		const merged = new Map();
		(this.filterValuesOverride.productType || []).forEach((value) => {
			if (!value || !value.label) return;
			merged.set(value.label, {
				label: value.label,
				count: value.count,
				input: value.input || { productType: value.label }
			});
		});
		values.forEach((value) => {
			if (!value || !value.label) return;
			const existing = merged.get(value.label);
			if (existing) {
				existing.count = Math.max(existing.count || 0, value.count || 0);
				if (value.input) existing.input = value.input;
			} else {
				merged.set(value.label, {
					label: value.label,
					count: value.count,
					input: value.input || { productType: value.label }
				});
			}
		});
		this.filterValuesOverride.productType = Array.from(merged.values());
		this.forceRebuild = false;
		if (this.allFilters.length) {
			this.updateFilters(this.allFilters);
		}
	}

	setTagValuesOverride(values) {
		this.filterValuesOverride.tag = Array.isArray(values) ? filterMoreFilterTagValues(values) : null;
		this.forceRebuild = true;
		if (this.allFilters.length) {
			this.updateFilters(this.allFilters);
		}
	}

	mergeTagValuesOverride(values) {
		if (!Array.isArray(values) || values.length === 0) return;
		const merged = new Map();
		(this.filterValuesOverride.tag || []).forEach((value) => {
			if (!value || !value.label || isExcludedMoreFilterTag(value.label)) return;
			merged.set(value.label, {
				label: value.label,
				count: value.count,
				input: value.input || { tag: value.label }
			});
		});
		filterMoreFilterTagValues(values).forEach((value) => {
			const existing = merged.get(value.label);
			if (existing) {
				existing.count = Math.max(existing.count || 0, value.count || 0);
				if (value.input) existing.input = value.input;
			} else {
				merged.set(value.label, {
					label: value.label,
					count: value.count,
					input: value.input || { tag: value.label }
				});
			}
		});
		this.filterValuesOverride.tag = Array.from(merged.values());
		this.forceRebuild = false;
		if (this.allFilters.length) {
			this.updateFilters(this.allFilters);
		}
	}

	getVendorOverrideCount(label) {
		if (!label || !Array.isArray(this.filterValuesOverride.vendor)) return null;
		const want = vendorMergeKey(label);
		if (!want) return null;
		const match = this.filterValuesOverride.vendor.find(
			(value) => value && vendorMergeKey(value.label) === want
		);
		if (!match) return null;
		return typeof match.count === 'number' ? match.count : null;
	}

	getProductTypeOverrideCount(label) {
		if (!label || !Array.isArray(this.filterValuesOverride.productType)) return null;
		const want = vendorMergeKey(label);
		if (!want) return null;
		const match = this.filterValuesOverride.productType.find(
			(value) => value && vendorMergeKey(value.label) === want
		);
		if (!match) return null;
		return typeof match.count === 'number' ? match.count : null;
	}

	getTagOverrideCount(label) {
		if (!label || !Array.isArray(this.filterValuesOverride.tag)) return null;
		const want = vendorMergeKey(label);
		if (!want) return null;
		const match = this.filterValuesOverride.tag.find((value) => value && vendorMergeKey(value.label) === want);
		if (!match) return null;
		return typeof match.count === 'number' ? match.count : null;
	}

	/**
	 * Same vendor / product type / tag sets as applyClientSideFilters (main-collection-graphql-quick-buy.liquid).
	 */
	_getActiveVendorTypeTagSets() {
		const normalize = (value) => String(value || '').trim().toLowerCase();
		const vendorSet = new Set();
		const typeSet = new Set();
		const tagSet = new Set();
		Object.entries(this.activeFilters).forEach(([filterId, values]) => {
			if (!values || !values.length) return;
			const id = String(filterId);
			const isVendor = id.includes('vendor');
			const isType = id.includes('product_type');
			const isTag = isShopifyTagFilterKey(filterId);
			if (!isVendor && !isType && !isTag) return;
			values.forEach((value) => {
				const label =
					typeof value === 'string'
						? value
						: value && typeof value === 'object'
							? value.value
							: null;
				if (!label) return;
				const normalized = normalize(label);
				if (!normalized) return;
				if (isVendor) vendorSet.add(normalized);
				if (isType) typeSet.add(normalized);
				if (isTag) tagSet.add(normalized);
			});
		});
		return { vendorSet, typeSet, tagSet, normalize };
	}

	/**
	 * Count row for Brand: candidate vendor must match; apply active product-type + tag constraints.
	 * Count row for Product type: candidate type must match; apply active vendor + tag constraints (ignore active type set for that row).
	 */
	_productPassesFacetRowCount(product, sets, mode) {
		const { vendorSet, typeSet, normalize } = sets;
		const v = normalize(product.vendor);
		const pt = normalize(product.productType);
		if (mode.kind === 'vendorCandidate') {
			if (v !== normalize(mode.vendorLabel)) return false;
			if (typeSet.size && !typeSet.has(pt)) return false;
		} else if (mode.kind === 'productTypeCandidate') {
			if (pt !== normalize(mode.typeLabel)) return false;
			if (vendorSet.size && !vendorSet.has(v)) return false;
		}
		return productMatchesActiveTagFilterGroups(product, this.activeFilters);
	}

	/**
	 * Hide Product type rows with (0) unless the option is checked (so selections stay visible to clear).
	 */
	_setProductTypeOptionVisibility(checkbox, count) {
		const opt = checkbox.closest('.qb-filter-option');
		if (!opt) return;
		opt.hidden = count === 0 && !checkbox.checked;
	}

	_syncProductTypeOptionVisibilityFromDom() {
		const form = document.getElementById('QuickBuyFilterForm');
		if (!form) return;
		form.querySelectorAll('.qb-filter-group:not(.qb-filter-brand) input[type="checkbox"][data-filter-id]').forEach((checkbox) => {
			const fid = checkbox.getAttribute('data-filter-id');
			if (!fid || !String(fid).includes('product_type')) return;
			/* 07.09.2026: Count aus den DATEN statt aus dem DOM-Text lesen —
			   für gesperrte Besucher steht dort nur der maskierte Platzhalter
			   "(––)", der faelschlich als 0 galt und ALLE Typ-Optionen
			   versteckte. Die echten Zahlen bleiben aus dem HTML draussen. */
			const label = checkbox.getAttribute('data-filter-value');
			let count = null;
			if (typeof this.getProductTypeOverrideCount === 'function') {
				count = this.getProductTypeOverrideCount(label);
			}
			if (count === null || count === undefined) {
				const f = (this.allFilters || []).find((x) => String(x.id).includes('product_type'));
				const v = f && (f.values || []).find((x) => facetLabelsEqual(x.label, label));
				count = v && v.count !== null && v.count !== undefined ? v.count : 0;
			}
			this._setProductTypeOptionVisibility(checkbox, count);
		});
	}

	/**
	 * When filters are active, Storefront collection facet counts often stay unscoped while the product list uses
	 * search query + filters. Recompute Brand + Product type counts from the full collection catalog.
	 */
	recomputeVendorAndProductTypeCountsFromCatalog() {
		const catalog = this.filterDependencyCatalog;
		if (!catalog || !catalog.length || !this.hasActiveFilters()) return;
		if (this.searchManager && typeof this.searchManager.isSearchActive === 'function' && this.searchManager.isSearchActive()) {
			return;
		}
		const form = document.getElementById('QuickBuyFilterForm');
		if (!form) return;
		const sets = this._getActiveVendorTypeTagSets();

		form.querySelectorAll('.qb-filter-brand input[type="checkbox"][data-filter-id]').forEach((checkbox) => {
			const fid = checkbox.getAttribute('data-filter-id');
			if (!fid || !String(fid).includes('vendor')) return;
			const vendorLabel = checkbox.getAttribute('data-filter-value');
			if (!vendorLabel) return;
			const count = catalog.filter((p) =>
				this._productPassesFacetRowCount(p, sets, { kind: 'vendorCandidate', vendorLabel })
			).length;
			const countSpan = checkbox.closest('.qb-filter-option')?.querySelector('.qb-filter-option-count');
			if (countSpan) countSpan.textContent = `${qbFmtCount(count)}`;
			this._setProductTypeOptionVisibility(checkbox, count);
		});

		form.querySelectorAll('.qb-filter-group:not(.qb-filter-brand) input[type="checkbox"][data-filter-id]').forEach((checkbox) => {
			const fid = checkbox.getAttribute('data-filter-id');
			if (!fid || !String(fid).includes('product_type')) return;
			const typeLabel = checkbox.getAttribute('data-filter-value');
			if (!typeLabel) return;
			const count = catalog.filter((p) =>
				this._productPassesFacetRowCount(p, sets, { kind: 'productTypeCandidate', typeLabel })
			).length;
			const countSpan = checkbox.closest('.qb-filter-option')?.querySelector('.qb-filter-option-count');
			if (countSpan) countSpan.textContent = `${qbFmtCount(count)}`;
			this._setProductTypeOptionVisibility(checkbox, count);
		});
	}

	/**
	 * Update only the filter counts without rebuilding the entire UI.
	 * When filters are active, prefer recomputed counts from the full collection catalog for Brand + Product type
	 * (Storefront facet counts may not match search-scoped product queries). Override counts apply when no filters.
	 */
	updateFilterCounts(filters) {
		const hasActiveFilters = this.hasActiveFilters();
		const catalogReady = this._hasFilterDependencyCatalog();
		const tagOnlyActive = this._isTagOnlyFilterSelection();
		// Tag-only uses collection.products(filters: tag) — Storefront Brand/Product type facet counts are often empty/wrong.
		const skipGraphqlVendorTypeCounts =
			tagOnlyActive || (hasActiveFilters && catalogReady);

		const isBrandLike = (filter) => {
			if (!filter) return false;
			const label = (filter.label || '').toLowerCase();
			return label.includes('brand') || label.includes('vendor') || (filter.id && String(filter.id).includes('vendor'));
		};

		const isProductTypeLike = (filter) => {
			if (!filter) return false;
			const label = (filter.label || '').toLowerCase();
			return label.includes('product type') || (filter.id && String(filter.id).includes('product_type'));
		};

		if (filters && filters.length > 0) {
			filters.forEach(filter => {
				if (!filter.values) return;

				const isBrand = isBrandLike(filter);
				const isType = isProductTypeLike(filter);

				if (skipGraphqlVendorTypeCounts && (isBrand || isType)) return;

				filter.values.forEach(value => {
					let count = this._normalizeFacetCount(value.count);

					if (!hasActiveFilters) {
						if (isBrand && value && value.label) {
							const overrideCount = this.getVendorOverrideCount(value.label);
							if (overrideCount !== null) count = overrideCount;
						} else if (isType && value && value.label) {
							const overrideCount = this.getProductTypeOverrideCount(value.label);
							if (overrideCount !== null) count = overrideCount;
						}
					}

					const filterForm = document.getElementById('QuickBuyFilterForm');
					const fid = String(filter.id);
					if (filterForm) {
						filterForm.querySelectorAll('input[type="checkbox"][data-filter-id]').forEach((checkbox) => {
							if (checkbox.getAttribute('data-filter-id') !== fid) return;
							const domVal = checkbox.getAttribute('data-filter-value');
							if (!facetLabelsEqual(domVal, value.label)) return;
							const countSpan = checkbox.closest('.qb-filter-option')?.querySelector('.qb-filter-option-count');
							if (countSpan) {
								countSpan.textContent = `${qbFmtCount(count)}`;
							}
							if (isType || isBrand) {
								this._setProductTypeOptionVisibility(checkbox, count);
							}
						});
					}
				});
			});
		}

		if (hasActiveFilters && catalogReady) {
			this.recomputeVendorAndProductTypeCountsFromCatalog();
		}
	}

	/**
	 * Restore filter checkboxes based on activeFilters state
	 */
	restoreFilterState() {
		const filterForm = document.getElementById('QuickBuyFilterForm');
		if (!filterForm) return;

		const checkboxes = filterForm.querySelectorAll('input[type="checkbox"]');
		checkboxes.forEach((checkbox) => {
			const filterId = checkbox.getAttribute('data-filter-id');
			const filterValue = checkbox.getAttribute('data-filter-value');
			const tagsRaw = checkbox.getAttribute('data-filter-tags');

			const activeValues = this.activeFilters[filterId] || [];
			const isActive = activeValues.some((item) => {
				if (typeof item === 'string') return item === filterValue;
				if (item && typeof item === 'object') {
					if (item.value === filterValue) return true;
					if (Array.isArray(item.tags) && tagsRaw) {
						try {
							return JSON.stringify(item.tags) === tagsRaw;
						} catch (e) {
							return false;
						}
					}
				}
				return false;
			});
			if (isActive) {
				checkbox.checked = true;
			}
		});

		// Update selected count badges after restoring state
		this.updateSelectedCountBadges();
		this._syncProductTypeOptionVisibilityFromDom();
	}

	/**
	 * Apply filters when checkboxes change
	 */
	applyFilters() {
		const filterForm = document.getElementById('QuickBuyFilterForm');
		if (!filterForm) return;

		// Check if search is active - if yes, clear it silently (without triggering reload)
		if (this.searchManager && this.searchManager.isSearchActive()) {
			this.searchManager.clearSearchSilent();
		}

		const prevFacetSig = this._prevFacetDimensionSignature;
		this.activeFilters = this.collectFilterStateFromForm();
		const nextFacetSig = this.buildFacetDimensionSignature(this.activeFilters);
		if (prevFacetSig !== undefined && prevFacetSig !== nextFacetSig && typeof window !== 'undefined') {
			window.__qbInvalidateSearchFacetCache = true;
		}
		this._prevFacetDimensionSignature = nextFacetSig;

		// If all filters are cleared, reset total count to original
		if (Object.keys(this.activeFilters).length === 0) {
			if(window.COLLECTION_TOTAL_COUNT_FROM_GRAPHQL) {
				window.COLLECTION_TOTAL_COUNT = window.COLLECTION_TOTAL_COUNT_FROM_GRAPHQL;
			}
		}

		// Update selected count badges for all filter groups
		this.updateSelectedCountBadges();

		// Trigger callback (this will reload with filter only, no search)
		this.onFilterChange();

		queueMicrotask(() => {
			if (this.hasActiveFilters() && this._hasFilterDependencyCatalog()) {
				this.recomputeVendorAndProductTypeCountsFromCatalog();
			}
			if (typeof this.refreshDependentTagCounts === 'function') {
				this.refreshDependentTagCounts();
			}
		});
	}

	/**
	 * Update selected count badges in filter group summaries
	 */
	updateSelectedCountBadges() {
		const filterGroups = document.querySelectorAll('.qb-filter-group');

		filterGroups.forEach(group => {
			const checkboxes = group.querySelectorAll('input[type="checkbox"]');
			if (checkboxes.length === 0) return;

			let selectedCount = 0;
			if (group.classList.contains('qb-more-filters-outer')) {
				const selA = group.querySelectorAll(
					`.qb-filter-subgroup[data-filter-id="${FILTER_TAG_GROUP_A_ID}"] input[type="checkbox"]:checked`
				).length;
				const selB = group.querySelectorAll(
					`.qb-filter-subgroup[data-filter-id="${FILTER_TAG_GROUP_B_ID}"] input[type="checkbox"]:checked`
				).length;
				selectedCount = selA + selB;
			} else {
				const firstCheckbox = checkboxes[0];
				const filterId = firstCheckbox.getAttribute('data-filter-id');
				selectedCount = this.activeFilters[filterId] ? this.activeFilters[filterId].length : 0;
			}

			// Find or create selected-count element in summary
			const summary = group.querySelector('summary');
			if (!summary) return;

			let countBadge = summary.querySelector('.selected-count');

			if (selectedCount > 0) {
				if (!countBadge) {
					// Create badge if it doesn't exist
					countBadge = document.createElement('div');
					countBadge.className = 'selected-count';
					countBadge.innerHTML = `<span>(${selectedCount})</span>`;
					summary.appendChild(countBadge);
				} else {
					// Update existing badge
					countBadge.innerHTML = `<span>(${selectedCount})</span>`;
				}
			} else {
				// Remove badge if no selections
				if (countBadge) {
					countBadge.remove();
				}
			}
		});

		// Update clear button visibility and count
		this.updateClearButton();
	}

	/**
	 * Update Clear Filters button visibility and count
	 */
	updateClearButton() {
		const clearWrap = document.getElementById('QuickBuyClearWrap');
		const clearCount = document.getElementById('QuickBuyClearCount');

		if (!clearWrap) return;

		const totalSelected = this.getTotalSelectedCount();

		if (totalSelected > 0) {
			clearWrap.style.display = '';
			if (clearCount) {
				clearCount.textContent = `(${totalSelected})`;
			}
		} else {
			clearWrap.style.display = 'none';
		}
	}

	/**
	 * Setup filter handlers
	 */
	hydrateFilterHandlers() {
		const filterForm = document.getElementById('QuickBuyFilterForm');
		if (!filterForm) return;

		filterForm.addEventListener('submit', (e) => e.preventDefault());

		// Sorting dropdown change handler
		const sortSelect = document.getElementById('QuickBuySortSelect');
		if (sortSelect) {
			sortSelect.addEventListener('change', (e) => {
				this.currentSortKey = e.target.value;
				this.onFilterChange(); // Trigger reload with new sort
			});
		}

		// Page size dropdown change handler
		const pageSizeSelect = document.getElementById('QuickBuyPageSizeSelect');
		if (pageSizeSelect) {
			pageSizeSelect.addEventListener('change', (e) => {
				this.currentPageSize = parseInt(e.target.value, 10);
				this.onFilterChange(); // Trigger reload with new page size
			});
		}

		// Checkbox change handler
		const checkboxes = filterForm.querySelectorAll('input[type="checkbox"]');
		checkboxes.forEach((el) => {
			el.addEventListener('change', () => this.applyFilters());
		});

		// Clear all filters button handler
		const clearButton = document.getElementById('QuickBuyClearAll');
		if (clearButton) {
			clearButton.addEventListener('click', () => {
				this.clearFilters();
			});
		}

		// Search within filter groups
		const filterGroups = filterForm.querySelectorAll('.qb-filter-group');
		filterGroups.forEach((group) => {
			const searchInput = group.querySelector('.qb-filter-search__input');
			if (!searchInput) return;

			const options = group.querySelectorAll('.qb-filter-option');

			let timeout;
			const filterOptions = (term) => {
				const q = term.trim().toLowerCase();
				options.forEach((lbl) => {
					const text = lbl.textContent.toLowerCase();
					lbl.style.display = text.includes(q) ? '' : 'none';
				});
			};

			searchInput.addEventListener('input', () => {
				clearTimeout(timeout);
				timeout = setTimeout(() => filterOptions(searchInput.value), 150);
			});
		});

		// "See more" button handler for brand filter
		const seeMoreButtons = filterForm.querySelectorAll('.qb-see-more-btn');
		seeMoreButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				const filterGroup = button.closest('.qb-filter-group');
				const filterValues = filterGroup.querySelector('.qb-filter-values');
				const textSpan = button.querySelector('.qb-see-more-text');

				// Toggle expanded state
				const isExpanded = filterValues.classList.contains('expanded');

				if (isExpanded) {
					filterValues.classList.remove('expanded');
					button.classList.remove('expanded');
					textSpan.textContent = ((window.QB_I18N||{}).filter_see_more) || 'See more';
				} else {
					filterValues.classList.add('expanded');
					button.classList.add('expanded');
					textSpan.textContent = ((window.QB_I18N||{}).filter_see_less) || 'See less';
				}
			});
		});
	}

	/**
	 * Repaint per-row "(N)" counts for Brand / Product type / Tag rows from the
	 * unscoped override caches (filterValuesOverride.{vendor,productType,tag}).
	 *
	 * Why: while filters are active, recomputeVendorAndProductTypeCountsFromCatalog()
	 * writes "(0)" into the DOM for vendor rows that don't match the active filter
	 * combination. The next refresh path (updateFilterCounts) only updates rows that
	 * are present in the new GraphQL filter response — missing rows keep their stale
	 * "(0)". On Clear filters we therefore reset every row directly from the override
	 * caches, which always hold the unscoped counts (populated by initFilterOverrides
	 * + mergeFiltersFromProducts).
	 */
	resetFilterCountsFromOverrides() {
		const filterForm = document.getElementById('QuickBuyFilterForm');
		if (!filterForm) return;

		const writeCount = (checkbox, count) => {
			const opt = checkbox.closest('.qb-filter-option');
			if (!opt) return;
			const countSpan = opt.querySelector('.qb-filter-option-count');
			if (countSpan) countSpan.textContent = `${qbFmtCount(count || 0)}`;
		};

		filterForm.querySelectorAll('input[type="checkbox"][data-filter-id]').forEach((checkbox) => {
			const fid = checkbox.getAttribute('data-filter-id') || '';
			const label = checkbox.getAttribute('data-filter-value');
			if (!label) return;

			let count = null;
			if (fid.includes('vendor') && typeof this.getVendorOverrideCount === 'function') {
				count = this.getVendorOverrideCount(label);
			} else if (fid.includes('product_type') && typeof this.getProductTypeOverrideCount === 'function') {
				count = this.getProductTypeOverrideCount(label);
			} else if (fid.includes('tag') && typeof this.getTagOverrideCount === 'function') {
				count = this.getTagOverrideCount(label);
			}
			if (count === null || count === undefined) return;
			writeCount(checkbox, count);

			// Re-show rows that were hidden because their scoped count was 0.
			if (fid.includes('product_type') || fid.includes('vendor')) {
				this._setProductTypeOptionVisibility(checkbox, count);
			}
		});
	}

	/**
	 * Clear all active filters
	 */
	clearFilters() {
		this.activeFilters = {};
		this._prevFacetDimensionSignature = this.buildFacetDimensionSignature(this.activeFilters);
		if (typeof window !== 'undefined') window.__qbInvalidateSearchFacetCache = true;
		const filterForm = document.getElementById('QuickBuyFilterForm');
		if (filterForm) {
			const checkboxes = filterForm.querySelectorAll('input[type="checkbox"]:checked');
			checkboxes.forEach(cb => cb.checked = false);
		}

		if(window.COLLECTION_TOTAL_COUNT_FROM_GRAPHQL) {
			window.COLLECTION_TOTAL_COUNT = window.COLLECTION_TOTAL_COUNT_FROM_GRAPHQL;
		}

		// Reset all per-row counts immediately from the unscoped override caches
		// so stale "(0)" badges (left over from recomputeVendorAndProductTypeCountsFromCatalog)
		// disappear without waiting for a full page reload.
		this.resetFilterCountsFromOverrides();

		// Update badges to remove all selected counts
		this.updateSelectedCountBadges();

		this.onFilterChange();
	}

	/**
	 * Clear all active filters silently (without triggering callbacks)
	 * Used when search starts and we want to clear filters without reload
	 */
	clearFiltersSilent() {
		this.activeFilters = {};
		this._prevFacetDimensionSignature = this.buildFacetDimensionSignature(this.activeFilters);
		if (typeof window !== 'undefined') window.__qbInvalidateSearchFacetCache = true;
		const filterForm = document.getElementById('QuickBuyFilterForm');
		if (filterForm) {
			const checkboxes = filterForm.querySelectorAll('input[type="checkbox"]:checked');
			checkboxes.forEach(cb => cb.checked = false);
		}
		if(window.COLLECTION_TOTAL_COUNT_FROM_GRAPHQL) {
			window.COLLECTION_TOTAL_COUNT = window.COLLECTION_TOTAL_COUNT_FROM_GRAPHQL;
		}

		// Same fix as clearFilters: also reset per-row counts here so the silent
		// clear path (e.g. when starting a search) leaves a consistent UI state.
		this.resetFilterCountsFromOverrides();

		// Update badges to remove all selected counts
		this.updateSelectedCountBadges();
	}

	/**
	 * Check if any filters are active
	 */
	hasActiveFilters() {
		return Object.keys(this.activeFilters).length > 0;
	}

	/** Whether any Shopify "tag" (More filters) dimension is selected — not arbitrary filter ids containing "tag". */
	hasSelectedMoreFiltersTags() {
		return Object.keys(this.activeFilters || {}).some((k) => isShopifyTagFilterKey(k));
	}

	/** True when only More-filters tag(s) are selected — no Brand or Product type. */
	_isTagOnlyFilterSelection() {
		if (!this.hasActiveFilters()) return false;
		if (!this.hasSelectedMoreFiltersTags()) return false;
		return !this.buildFacetDimensionSignature(this.activeFilters);
	}

	_hasFilterDependencyCatalog() {
		return Array.isArray(this.filterDependencyCatalog) && this.filterDependencyCatalog.length > 0;
	}

	_normalizeFacetCount(raw) {
		const n = Number(raw);
		return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
	}

	/**
	 * Get current active filters
	 */
	getActiveFilters() {
		return this.activeFilters;
	}

	/**
	 * Get current sort key
	 */
	getSortKey() {
		return this.currentSortKey;
	}

	/**
	 * Get current page size
	 */
	getPageSize() {
		return this.currentPageSize;
	}

	/**
	 * Convert Shopify sort key to GraphQL format
	 * Handles reverse sorting for DESC variants
	 * @param {{ forProductsQuery?: boolean }} [options] - Root `products(query:)` uses ProductSortKeys (CREATED_AT); collection.products uses ProductCollectionSortKeys (CREATED).
	 */
	getSortKeyForGraphQL(options = {}) {
		const forProductsQuery = options.forProductsQuery === true;
		let baseKey = this.currentSortKey.endsWith('_DESC')
			? this.currentSortKey.replace('_DESC', '')
			: this.currentSortKey;
		const reverse = this.currentSortKey.endsWith('_DESC');
		if (forProductsQuery && baseKey === 'CREATED') {
			baseKey = 'CREATED_AT';
		}
		return { sortKey: baseKey, reverse };
	}

	/**
	 * Reset sort to Title A-Z
	 * Used when search starts with date sorting active
	 */
	resetSortToTitle() {
		this.currentSortKey = 'TITLE';

		// Update the dropdown UI if it exists
		const sortSelect = document.getElementById('QuickBuySortSelect');
		if (sortSelect) {
			sortSelect.value = 'TITLE';
		}
	}
}

// Export to global scope
window.QuickBuyFiltersManager = QuickBuyFiltersManager;

